import { Injectable } from '@angular/core';
import { LocalesService } from '@core/transloco/locales.service';
import { TenantService } from '@core/tenant/tenant.service';
import { ApiService } from '@targx/services/api/api.service';
import { map, Observable, of } from 'rxjs';
import { DealerPageListingConfig } from '@core/tenant/dealer-site.model';

export type ProductAvailability =
  'all' | 'in_stock' | 'sold' | 'restoration' | 'reserved';

const SORT_MAP: Record<string, string> = {
  newest: 'updatedAt:desc',
  oldest: 'updatedAt:asc',
  price_asc: 'price:asc',
  price_desc: 'price:desc',
};

@Injectable({ providedIn: 'root' })
export class DealerDataService {
  constructor(
    private _api: ApiService,
    private _localeService: LocalesService,
    private _tenantService: TenantService,
  ) {}

  private get _sellerId(): number | null {
    return this._tenantService.profile?.id ?? null;
  }

  private get _locale(): string {
    return this._localeService.getActiveLocale()?.code || 'pt-PT';
  }

  /** MicroSiteHomepage pelo slug da DealerSite.homePage */
  getHomepage(): Observable<any> {
    const slug = this._tenantService.profile?.dealerSite?.homePage?.slug;
    const data: any = {
      locale: this._locale,
      'populate[banners][populate]': '*',
      'populate[content][populate]': '*',
    };
    if (slug) data['filters[slug][$eq]'] = slug;
    return this._api.get('/micro-site-homepages', data);
  }

  /** Produtos do dealer filtrados por availability */
  getProducts(
    availability: ProductAvailability,
    page = 1,
    limit = 12,
    productType?: string,
    sortBy?: string,
  ): Observable<any> {
    const data: any = {
      locale: this._locale,
      'populate[images]': '*',
      'populate[product_brand]': '*',
      'populate[product_model]': '*',
      'populate[product_condition]': '*',
      'pagination[page]': page,
      'pagination[pageSize]': limit,
      sort: SORT_MAP[sortBy] ?? 'updatedAt:desc',
      'filters[approved][$eq]': true,
    };
    if (this._sellerId) data['filters[seller][id][$eq]'] = this._sellerId;
    if (availability && availability !== 'all')
      data['filters[availability][$eq]'] = availability;
    if (productType) data['filters[productType][$eq]'] = productType;
    return this._api.get('/products', data);
  }

  /** Produtos a partir de um listing_config (Strapi-first, agnóstico) */
  getProductsFromConfig(
    config: DealerPageListingConfig,
    page = 1,
  ): Observable<any> {
    const data: any = {
      locale: this._locale,
      'populate[images]': '*',
      'populate[product_brand]': '*',
      'populate[product_model]': '*',
      'populate[product_condition]': '*',
      'pagination[page]': page,
      'pagination[pageSize]': config.product_number ?? 24,
      sort: SORT_MAP[config.sort_by] ?? 'updatedAt:desc',
      'filters[approved][$eq]': true,
    };

    if (this._sellerId) data['filters[seller][id][$eq]'] = this._sellerId;
    if (config.availability && config.availability !== 'all') {
      data['filters[availability][$eq]'] = config.availability;
    }
    if (config.product_category?.id) {
      data['filters[product_category][id][$eq]'] = config.product_category.id;
    }
    if (config.product_category?.productType) {
      data['filters[productType][$eq]'] = config.product_category.productType;
    }
    if (config.product_type) {
      data['filters[productType][$eq]'] = config.product_type;
    }

    return this._api.get('/products', data);
  }

  /** Produtos por lista de IDs (ex.: resultado de pesquisa Solr), ordem preservada */
  getProductsByIds(ids: Array<string | number>): Observable<any> {
    if (!ids.length) return of({ data: [] });

    const data: any = {
      locale: this._locale,
      'populate[images]': '*',
      'populate[product_brand]': '*',
      'populate[product_model]': '*',
      'populate[product_condition]': '*',
      'pagination[pageSize]': ids.length,
      'filters[id][$in]': ids,
    };
    if (this._sellerId) data['filters[seller][id][$eq]'] = this._sellerId;

    return this._api.get('/products', data).pipe(
      map((res: any) => {
        const order = new Map(ids.map((id, index) => [String(id), index]));
        const sorted = [...(res?.data || [])].sort(
          (a, b) =>
            (order.get(String(a.id)) ?? 0) - (order.get(String(b.id)) ?? 0),
        );
        return { ...res, data: sorted };
      }),
    );
  }

  /** Produto único por ID */
  getProductById(id: string): Observable<any> {
    const data: any = {
      locale: this._locale,
      'populate[images]': '*',
      'populate[product_brand]': '*',
      'populate[product_model]': '*',
      'populate[product_condition]': '*',
      'populate[attributes]': '*',
      'populate[SEO]': '*',
    };
    return this._api.get(`/products/${id}`, data);
  }

  /** Artigos próprios do dealer */
  getArticles(page = 1, limit = 9): Observable<any> {
    const data: any = {
      locale: this._locale,
      'populate[featureImage]': '*',
      'populate[author]': '*',
      'populate[article_categories]': '*',
      'pagination[page]': page,
      'pagination[pageSize]': limit,
      sort: 'publishDate:desc',
      'filters[status][$eq]': 'Approved',
    };
    if (this._sellerId) data['filters[seller][id][$eq]'] = this._sellerId;
    return this._api.get('/articles', data);
  }

  /** Artigo por slug */
  getArticleBySlug(slug: string): Observable<any> {
    const data: any = {
      locale: this._locale,
      'filters[slug][$eq]': slug,
      'populate[featureImage]': '*',
      'populate[author]': '*',
      'populate[layout][populate]': '*',
      'populate[SEO]': '*',
    };
    return this._api.get('/articles', data);
  }

  /** Página do Strapi por slug (para /empresa, etc.) */
  getPage(slug: string): Observable<any> {
    const data: any = {
      locale: this._locale,
      'filters[slug][$eq]': slug,
      'populate[content][populate]': '*',
    };
    return this._api.get('/pages', data);
  }
}
